import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

//CSS
import "../0PageStyles.css";
import "./stockTakePage.css";

//Components
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import Title from "../../Components/Other Components/Title";
import PageLoader from "../../Components/Other Components/pageLoader";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";

//Functions and hooks
import {
  GetItemsInWarehouse,
  SubmitStockCount,
} from "../../BackendAccess/ItemHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { usePopup } from "../../Contexts/PopupContext";

export default function StockTakePage() {
  const [Warehouse, setWarehouse] = useState([]);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [submitBtnDisabled, setSubmitBtnDisabled] = useState(false);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const navigate = useNavigate();
  const { t } = useTranslation("items");
  const { showPopup, removePopup } = usePopup();

  const warehouseID = Warehouse?.[0] || null;

  const onWarehouseChange = async (warehouses) => {
    setWarehouse(warehouses);
    const id = warehouses?.[0] || null;
    if (!id) {
      setItems([]);
      setCounts(new Map());
      return;
    }
    setLoading(true);
    try {
      const [data, error] = await GetItemsInWarehouse(id);
      if (error) {
        setError(error);
        return;
      }
      setItems(data);
      setCounts(new Map(data.map((item) => [item.ItemID, item.Amount])));
    } finally {
      setLoading(false);
    }
  };

  const onCountChange = (ItemID, value) => {
    if (value !== "" && !/^[0-9]*$/.test(value)) return;
    setCounts((prev) => {
      const next = new Map(prev);
      next.set(ItemID, value === "" ? "" : parseInt(value));
      return next;
    });
  };

  const onSubmitClick = () => {
    if (!warehouseID) {
      setError({ err: t("Select a warehouse first."), errCode: 400 });
      return;
    }

    const changedCounts = items
      .map((item) => ({
        ItemID: item.ItemID,
        CountedAmount: counts.get(item.ItemID),
        SystemAmount: item.Amount,
      }))
      .filter(
        (row) =>
          row.CountedAmount !== "" &&
          row.CountedAmount !== undefined &&
          row.CountedAmount !== row.SystemAmount
      );

    if (changedCounts.length === 0) {
      setError({
        err: t("No counts have changed from the current system stock."),
        errCode: 400,
      });
      return;
    }

    const key = Date.now();
    showPopup(
      <PopupConfirmation
        message={
          <p>
            {t(
              "Are you sure you want to submit this stock count? {{count}} item(s) will be adjusted.",
              { count: changedCounts.length }
            )}
          </p>
        }
        onConfirm={() => onConfirmSubmit(key, changedCounts)}
        onCancel={() => removePopup(key)}
      />,
      key
    );
  };

  const onConfirmSubmit = async (key, changedCounts) => {
    setSubmitBtnDisabled(true);
    showNotification(t("Submitting stock count..."), "info");
    try {
      const payload = changedCounts.map(({ ItemID, CountedAmount }) => ({
        ItemID,
        CountedAmount,
      }));
      const [data, error] = await SubmitStockCount(warehouseID, payload);
      if (error) {
        setError(error);
        return;
      }
      showNotification(
        t("Stock count submitted! {{count}} item(s) adjusted.", {
          count: data.adjustedCount,
        }),
        "success"
      );
      navigate(-1);
    } finally {
      setSubmitBtnDisabled(false);
      removePopup(key);
    }
  };

  const clickOnCancel = () => {
    navigate(-1);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Stock take", { ns: "tutorial" }),
      message: t(
        "This page lets you do a physical stock count of a warehouse and correct the system's records to match what you actually counted.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "stock-take-warehouse",
      title: t("Warehouse to count"),
      message: t(
        "Pick the warehouse you're counting. Once selected, every item currently stocked there will load below.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".stock-take-table",
      title: t("Enter counted quantities"),
      message: t(
        "Each row is one item, showing what the system currently thinks is in stock. Type the quantity you actually counted into the last column - leave it as-is for items you didn't recount.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "stock-take-submit-btn",
      title: t("Submit Count"),
      message: t(
        "Once you're done, click here. Only the items whose counted quantity differs from the system amount will be adjusted - everything else is left untouched.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="stockTake" tutorialSteps={tutorialSteps}>
      <div className="page-content">
        <div className="PageWarehousePickerContainer mt-3" data-tutorial-id="stock-take-warehouse">
          <WarehousePicker
            title={t("Warehouse to count")}
            onChange={onWarehouseChange}
            singleSelect={true}
            initWarehouses={[]}
          ></WarehousePicker>
        </div>

        <div className="container-bordered w-90 mt-4 mb-4 vertical">
          <Title className="font-m mb-2">{t("Enter counted quantities")}</Title>
          <PageLoader loading={loading} error={pageError}>
            {!warehouseID ? (
              <p className="cardPlaceholder">{t("Select a warehouse first.")}</p>
            ) : items.length === 0 ? (
              <p className="cardPlaceholder">
                {t("This warehouse has no stock to count.")}
              </p>
            ) : (
              <table className="stock-take-table">
                <thead>
                  <tr>
                    <th>{t("ID")}</th>
                    <th>{t("Name")}</th>
                    <th>{t("Current Stock")}</th>
                    <th>{t("Counted Quantity")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.ItemID}>
                      <td>{item.ItemID}</td>
                      <td>{item.Name}</td>
                      <td>{item.Amount}</td>
                      <td className="stock-take-count-cell">
                        <InputField
                          value={counts.get(item.ItemID)}
                          onChange={(v) => onCountChange(item.ItemID, v)}
                          copyEnabled={false}
                          className="mb-0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </PageLoader>
        </div>

        <div className="btn-container">
          <Button
            btnMargin={"btn-default"}
            btnStyle="btn-positive"
            onClick={onSubmitClick}
            disabled={submitBtnDisabled || !warehouseID}
            acceptEnter={true}
            icon={"apply"}
            tutorialId="stock-take-submit-btn"
          >
            {t("Submit Count")}
          </Button>
          <Button
            btnMargin={"btn-default"}
            btnStyle="btn-primary"
            onClick={clickOnCancel}
          >
            {t("Cancel", { ns: "common" })}
          </Button>
        </div>
      </div>
    </PageWrapper>
  );
}
